import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as adminService from '../../services/admin';

const UserPermissions = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState({
        role_permissions: [],
        granted_permissions: [],
        revoked_permissions: []
    });
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState('');

    useEffect(() => {
        loadUsers();
        loadPermissions();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await adminService.listUsers();
            if (response.status === 'success') {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error loading users:', error);
            // toast.error('Failed to load users');
        }
    };

    const loadPermissions = async () => {
        try {
            const response = await adminService.listPermissions();
            if (response.status === 'success') {
                setPermissions(response.data);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            // toast.error('Failed to load permissions');
        }
    };

    const loadUserPermissions = async (userId) => {
        try {
            setLoading(true);
            const response = await adminService.listUserPermissions(userId);
            if (response.status === 'success') {
                setSelectedUser(response.data.user);
                setUserPermissions({
                    role_permissions: response.data.role_permissions,
                    granted_permissions: response.data.granted_permissions,
                    revoked_permissions: response.data.revoked_permissions
                });
            }
        } catch (error) {
            console.error('Error loading user permissions:', error);
            // toast.error('Failed to load user permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (e) => {
        const userId = e.target.value;
        if (userId) {
            loadUserPermissions(userId);
        } else {
            setSelectedUser(null);
            setUserPermissions({
                role_permissions: [],
                granted_permissions: [],
                revoked_permissions: []
            });
        }
    };

    const handleGrantPermission = async () => {
        if (!selectedPermission) {
            // toast.error('Please select a permission');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.grantUserPermission(selectedUser.id, {
                permission_id: parseInt(selectedPermission)
            });

            if (response.status === 'success') {
                // toast.success('Permission granted successfully');
                loadUserPermissions(selectedUser.id);
                setShowGrantModal(false);
                setSelectedPermission('');
            } else {
                // toast.error(response.message || 'Failed to grant permission');
            }
        } catch (error) {
            console.error('Error granting permission:', error);
            // toast.error('Failed to grant permission');
        } finally {
            setLoading(false);
        }
    };

    const handleRevokePermission = async (permissionId) => {
        try {
            setLoading(true);
            const response = await adminService.revokeUserPermission(selectedUser.id, {
                permission_id: permissionId
            });

            if (response.status === 'success') {
                // toast.success('Permission revoked successfully');
                loadUserPermissions(selectedUser.id);
            } else {
                // toast.error(response.message || 'Failed to revoke permission');
            }
        } catch (error) {
            console.error('Error revoking permission:', error);
            // toast.error('Failed to revoke permission');
        } finally {
            setLoading(false);
        }
    };

    const getAllPermissions = () => {
        const allPerms = [];
        Object.keys(permissions).forEach(module => {
            permissions[module].forEach(perm => {
                allPerms.push(perm);
            });
        });
        return allPerms;
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-user-shield"></i> User Permissions Management
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Select User</label>
                            <select
                                className="form-select"
                                onChange={handleUserChange}
                                disabled={loading}
                            >
                                <option value="">-- Select User --</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedUser && (
                            <div className="col-md-6 d-flex align-items-end">
                                <button
                                    className="btn btn-success"
                                    onClick={() => setShowGrantModal(true)}
                                    disabled={loading}
                                >
                                    <i className="fa fa-plus"></i> Grant Permission
                                </button>
                            </div>
                        )}
                    </div>

                    {selectedUser && (
                        <>
                            <div className="alert alert-info">
                                <strong>User:</strong> {selectedUser.name} ({selectedUser.email})
                                <br />
                                <strong>Role:</strong> {selectedUser.role?.role_name || 'N/A'}
                            </div>

                            <div className="row">
                                {/* Role Permissions */}
                                <div className="col-md-4 mb-3">
                                    <div className="card h-100">
                                        <div className="card-header bg-success text-white">
                                            <h6 className="mb-0">
                                                <i className="fa fa-check-circle"></i> Role Permissions
                                                <span className="badge bg-light text-dark ms-2">
                                                    {userPermissions.role_permissions.length}
                                                </span>
                                            </h6>
                                        </div>
                                        <div className="card-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                            {userPermissions.role_permissions.length > 0 ? (
                                                <ul className="list-unstyled">
                                                    {userPermissions.role_permissions.map(perm => (
                                                        <li key={perm.id} className="mb-2">
                                                            <span className="badge bg-success me-2">
                                                                {perm.module}
                                                            </span>
                                                            {perm.display_name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">No role permissions</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Granted Permissions */}
                                <div className="col-md-4 mb-3">
                                    <div className="card h-100">
                                        <div className="card-header bg-primary text-white">
                                            <h6 className="mb-0">
                                                <i className="fa fa-plus-circle"></i> Granted Permissions
                                                <span className="badge bg-light text-dark ms-2">
                                                    {userPermissions.granted_permissions.length}
                                                </span>
                                            </h6>
                                        </div>
                                        <div className="card-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                            {userPermissions.granted_permissions.length > 0 ? (
                                                <ul className="list-unstyled">
                                                    {userPermissions.granted_permissions.map(perm => (
                                                        <li key={perm.id} className="mb-2 d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <span className="badge bg-primary me-2">
                                                                    {perm.module}
                                                                </span>
                                                                {perm.display_name}
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleRevokePermission(perm.id)}
                                                                title="Revoke"
                                                            >
                                                                <i className="fa fa-times"></i>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">No granted permissions</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Revoked Permissions */}
                                <div className="col-md-4 mb-3">
                                    <div className="card h-100">
                                        <div className="card-header bg-danger text-white">
                                            <h6 className="mb-0">
                                                <i className="fa fa-ban"></i> Revoked Permissions
                                                <span className="badge bg-light text-dark ms-2">
                                                    {userPermissions.revoked_permissions.length}
                                                </span>
                                            </h6>
                                        </div>
                                        <div className="card-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                            {userPermissions.revoked_permissions.length > 0 ? (
                                                <ul className="list-unstyled">
                                                    {userPermissions.revoked_permissions.map(perm => (
                                                        <li key={perm.id} className="mb-2">
                                                            <span className="badge bg-danger me-2">
                                                                {perm.module}
                                                            </span>
                                                            {perm.display_name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">No revoked permissions</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {!selectedUser && (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-info-circle fa-3x mb-3"></i>
                            <p>Please select a user to manage permissions</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Grant Permission Modal */}
            {showGrantModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Grant Permission</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowGrantModal(false);
                                        setSelectedPermission('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <label className="form-label">Select Permission</label>
                                <select
                                    className="form-select"
                                    value={selectedPermission}
                                    onChange={(e) => setSelectedPermission(e.target.value)}
                                >
                                    <option value="">-- Select Permission --</option>
                                    {getAllPermissions().map(perm => (
                                        <option key={perm.id} value={perm.id}>
                                            [{perm.module}] {perm.display_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowGrantModal(false);
                                        setSelectedPermission('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleGrantPermission}
                                    disabled={loading}
                                >
                                    Grant Permission
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserPermissions;
